import { useRef, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useErrorBoundary } from 'react-error-boundary';

import {
  ESdkEvents,
  AdyenAPMStatusResponse,
  AdyenFinalizePaymentResponse,
  AppchargeErrorType,
  EBEPaymentMethodsName,
} from '../../../../../../../contracts';
import { sendFEEvent } from '../../../../../../../eventUtil';
import { useAPI } from '../../../../../../../services/api.service';
import { createAdyenAPMFinalizeRequestBody } from '../../../utils';
import { useCheckout } from '../../../../../../../hooks/use-checkout';
import { useOrderPolling } from '../../../../../hooks/use-order-polling';
import { usePaymentState } from '../../../../../hooks/use-payment-state';
import { useTabManager } from '../../../../../../../hooks/use-tab-manager';
import { getCheckoutUrl } from '../../../../../../../utils/get-checkout-url';

import { shouldUseBroadcastChannelForAPM } from '../../../../../../../utils/apm-broadcast-channel';
import { AdyenAPMAdditionalInputs } from '../../../types';
import { useAdyenOrderPolling } from './use-adyen-order-polling';
import { generateAppchargeErrorMessage } from '../../../../../../../utils/generate-appcharge-error-message';
import { apmLatencyTracker } from '../../../../../../../utils/apm-latency-tracker';

export function useAdyenAPM() {
  const isAdyenPaymentInProgress = useRef<any>(false);
  const hasHandledRedirectResult = useRef(false);
  const broadcastChannelCleanupRef = useRef<(() => void) | null>(null);
  const { selectedPaymentMethod, checkoutData, getEventParams } = useCheckout();
  const [isAdyenApmBroadcastLock, setIsAdyenApmBroadcastLock] = useState(false);
  const setIsApmLoading = usePaymentState((state) => state.setIsApmLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setIsPaymentElementLoading = usePaymentState((state) => state.setIsPaymentElementLoading);
  const { startOrderPolling } = useOrderPolling();
  const { startAdyenOrderPolling } = useAdyenOrderPolling();
  const { API } = useAPI();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const { tabManager } = useTabManager();
  const { showBoundary } = useErrorBoundary();

  useEffect(() => {
    try {
      apmLatencyTracker.startTracking('Adyen APM', selectedPaymentMethod?.id);
    } catch (error) {
      console.error('Failed to start APM latency tracking:', error);
    }

    setTimeout(() => {
      setIsPaymentElementLoading(false);
      setIsApmLoading(false);

      try {
        apmLatencyTracker.endTracking('Adyen APM', selectedPaymentMethod?.id, {
          paymentMethodName: selectedPaymentMethod?.paymentMethodName,
          paymentMethodDisplayName: selectedPaymentMethod?.paymentMethodDisplayName,
          provider: 'Adyen',
        });
      } catch (error) {
        console.error('Failed to end APM latency tracking:', error);
      }
    }, 300);
  }, [selectedPaymentMethod]);

  useEffect(() => {
    return () => {
      broadcastChannelCleanupRef.current?.();
      broadcastChannelCleanupRef.current = null;
    };
  }, []);

  const handleServerResponse = async (finalizePaymentResponse: AdyenFinalizePaymentResponse) => {
    const { status, action, message } = finalizePaymentResponse.adyen;
    try {
      await tabManager.isTabLoaded();
    } catch (error) {
      console.error('Failed to load tab:', error);
      handleError(finalizePaymentResponse.appChargeErrorType);
      return;
    }
    if (status === 'succeeded') {
      tabManager.closeTab();
      startOrderPolling(checkoutData?.orderId!);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
      });
    } else if (status === 'redirect') {
      hasHandledRedirectResult.current = false;
      tabManager.redirectToUrl(action?.url);
      startPaymentPolling();
    } else if (status === 'failed') {
      tabManager.closeTab();
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: message,
      });
      handleError(finalizePaymentResponse.appChargeErrorType);
    }
  };

  const startPaymentPolling = () => {
    const interval = setInterval(() => {
      if (tabManager.isTabClosed()) {
        clearInterval(interval);
        handleTabClosed();
      }
    }, 1000);
  };

  const handleTabClosed = () => {
    const redirectResult = tabManager.getQueryParams('redirectResult');
    if (redirectResult) {
      checkAPMStatus(redirectResult);
    } else if (
      selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.alipay_hk ||
      selectedPaymentMethod?.paymentMethodName === EBEPaymentMethodsName.alipay
    ) {
      setIsAdyenApmBroadcastLock(true);
      startAdyenOrderPolling(checkoutData?.orderId!, () => {
        handleError(AppchargeErrorType.GENERIC);
        setIsAdyenApmBroadcastLock(false);
      });
    } else {
      handleError(AppchargeErrorType.GENERIC);
    }
  };

  const startBroadcastChannelSubscription = () => {
    const orderId = checkoutData?.orderId!;

    setIsAdyenApmBroadcastLock(true);
    startAdyenOrderPolling(orderId);
  };

  const checkAPMStatus = async (redirectResult: string) => {
    try {
      const statusResponse = await API.getAdyenPaymentStatus<AdyenAPMStatusResponse>(
        redirectResult,
        selectedPaymentMethod?.id!,
      );
      handleStatusResponse(statusResponse);
    } catch (error) {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
      });
      handleError(AppchargeErrorType.GENERIC);
    }
  };

  const handleStatusResponse = (statusResponse: AdyenAPMStatusResponse) => {
    // NOTE! Adyen uses "Authorised" instead of "Authorized"
    if (statusResponse.resultCode === 'Authorised') {
      startOrderPolling(checkoutData?.orderId!);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
      });
    } else {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
      });
      handleError(AppchargeErrorType.GENERIC);
    }
  };

  const handlePay = async (additionalInputValues: AdyenAPMAdditionalInputs) => {
    if (isAdyenPaymentInProgress.current === true) {
      return;
    }
    isAdyenPaymentInProgress.current = true;
    try {
      setIsPaymentLoading(true);
      tabManager.openTab(`${getCheckoutUrl()}/apm-dynamic-page`);
      const finalizePaymentResponse = await API.finalizePayment<AdyenFinalizePaymentResponse>(
        createAdyenAPMFinalizeRequestBody({
          selectedPaymentMethod,
          additionalInputValues,
          country: checkoutData?.customer.geoLocator.countryCode2,
        }),
      );
      handleServerResponse(finalizePaymentResponse);
    } catch (error: any) {
      const errResponse = error?.response?.data;
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        reason: errResponse?.message,
        error: {
          isFatal: true,
          reason: errResponse?.message,
        },
      });
      showBoundary(t('unableToProcessPayment'));
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    isAdyenPaymentInProgress.current = false;
  };

  return {
    handlePay,
    isAdyenApmBroadcastLock,
  };
}
